import { useState, useContext, useEffect } from 'react'
import { ClientContext } from '../../../../contexts/clientProvider'
import { UserContext } from '../../../../contexts/userProvider'
import { getDadosFinanceiro, getComponentMap } from '../../../General/Format/cliente';
import FormComp from '../../../General/FormsComp';

const Financeiro = ({ client, setClient, handleFecharClick }) => {
    const { updateCliente } = useContext(ClientContext);
    const { isGerente } = useContext(UserContext);
    const [type, setType] = useState('Geral');
    const [editedData, setEditedData] = useState({});
    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleEditClick = () => {
        setType('Edit');
        setEditedData(client);
    };

    const handleSaveClick = async () => {
        setLoading(true);
        const newClient = await updateCliente(editedData);
        setClient(newClient);
        setLoading(false);
        setType('Geral');
        handleFecharClick();
    };

    const handleCancelClick = () => {
        setType('Geral');
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedData((prevData) => ({ ...prevData, [name]: value }));
    };

    const splitEmail = (email) => {
        if (email && email.trim() !== '') {
            let splitEmail = email.split(';');
            let newEmail = splitEmail.map((email, index) => {
                return <p key={index}>{email}</p>
            })
            return newEmail;
        }
        return null;
    }

    useEffect(() => {
        if (client) {
            setEditedData(client);
        }
    }, [client]);
    
    useEffect(() => {
        let newDados = getDadosFinanceiro(editedData, null); // No modo edição, não usar splitEmail
        setDados(newDados);
    }, [editedData]);

    // Campos legados congelados: continuam visíveis, mas não são mais editáveis
    // (valor e tipo de contrato agora vivem na aba "Contratos" — Leva 1).
    const CAMPOS_LEGADO_CONTRATO = ['valor_contrato', 'tipo_contrato'];
    const marcarComoLegado = (dado) => (
        CAMPOS_LEGADO_CONTRATO.includes(dado.name)
            ? { ...dado, titulo: `${dado.titulo} (legado — ver aba Contratos)`, disabled: true }
            : dado
    );

    // Obter dados baseado no tipo (visualização ou edição)
    const getDadosParaRenderizar = () => {
        if (type === 'Geral') {
            const dadosVis = getDadosFinanceiro(client, splitEmail);
            // Para visualização, ajustar valores vazios para mostrar "Nenhum dado cadastrado"
            return dadosVis.map(dado => marcarComoLegado({
                ...dado,
                valor: dado.valor || 'Nenhum dado cadastrado.'
            }));
        } else {
            const dadosEdit = getDadosFinanceiro(editedData, null); // No modo edição, não usar splitEmail

            // Ajustar o valor do campo tipo_contrato
            const tipoContratoField = dadosEdit.find(d => d.name === 'tipo_contrato');
            if (tipoContratoField) {
                tipoContratoField.valorSelect = editedData.tipo_contrato || '';
            }

            // valor_contrato/tipo_contrato são legado (congelado): exibir somente leitura,
            // sem permitir edição pela tela financeira.
            return dadosEdit.map(marcarComoLegado);
        }
    };

    const dadosParaRenderizar = getDadosParaRenderizar();

    // Componente personalizado para exibição de dados
    const DataCard = ({ titulo, valor, icon = null }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start space-x-3">
                {icon && (
                    <div className="flex-shrink-0 text-vox-blue-600 dark:text-vox-blue-400">
                        {icon}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {titulo}
                    </h3>
                    <div className="text-base font-semibold text-gray-900 dark:text-white break-words">
                        {valor || 'Nenhum dado cadastrado.'}
                    </div>
                </div>
            </div>
        </div>
    );

    // Ícones para cada campo financeiro
    const getFieldIcon = (fieldName) => {
        const icons = {
            responsavel_financeiro: '👨‍💼',
            telefone_financeiro: '📞',
            email_responsalvel_financeiro: '📧',
            valor_contrato: '💰',
            tipo_contrato: '📋'
        };
        return icons[fieldName] || '💼';
    };

    return (
        <div className="space-y-6">
            {/* Grid responsivo para os dados */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {type === 'Geral' ? (
                    // Modo visualização
                    dadosParaRenderizar.map((dado, index) => (
                        <DataCard
                            key={index}
                            titulo={dado.titulo}
                            valor={dado.valor}
                            icon={getFieldIcon(dado.name)}
                        />
                    ))
                ) : (
                    // Modo edição
                    dadosParaRenderizar.map((dado, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                            <FormComp 
                                dados={dado} 
                                handleInputChange={handleInputChange}
                                layout="modal"
                            />
                        </div>
                    ))
                )}
            </div>

            {/* Botões de ação */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                {type === 'Geral' ? (
                    <>
                        <button 
                            onClick={handleFecharClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✕</span>
                            Fechar
                        </button>
                        {isGerente && (
                        <button 
                            onClick={handleEditClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-vox-blue-600 hover:bg-vox-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✏️</span>
                            Editar
                        </button>
                        )}
                    </>
                ) : (
                    <>
                        <button 
                            onClick={handleCancelClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✕</span>
                            Cancelar
                        </button>
                        <button 
                            onClick={handleSaveClick}
                            disabled={loading}
                            className="flex-1 sm:flex-none px-6 py-3 bg-vox-green-600 hover:bg-vox-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <span className="mr-2">💾</span>
                                    Salvar
                                </>
                            )}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Financeiro;
